import { Injectable, EventEmitter } from "@angular/core";

import {
  DataArray,
  IDataArray,
  SignalRService,
  SignalREventService,
  ToastrService,
  Language,
} from "./../../shared/index";

import { User, Permission, SectionPermission } from "./../index";

@Injectable()
export class UserService {
  public usersChange: EventEmitter<User[]> = new EventEmitter<User[]>();
  public userChange: EventEmitter<User> = new EventEmitter<User>();
  public permissionChange: EventEmitter<Permission[]> = new EventEmitter<
    Permission[]
  >();
  public languageChange: EventEmitter<Language[]> = new EventEmitter<
    Language[]
  >();
  public userCreated: EventEmitter<User> = new EventEmitter<User>();
  public userSelectedChange: EventEmitter<User> = new EventEmitter<User>();
  public clearSelected: EventEmitter<boolean> = new EventEmitter<boolean>();
  public printChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  public minesiteids: EventEmitter<Int32Array> = new EventEmitter<Int32Array>();

  public users: DataArray<User> = new DataArray<User>();
  public permissions: DataArray<Permission> = new DataArray<Permission>();
  public languages: DataArray<Language> = new DataArray<Language>();
  public sections: string[] = [];

  public ascending = true;
  public orderBy = "FirstName";
  public currentPage = 1;
  public totalUsers = 0;

  private name = "";
  private allEmployees = true;
  private selectedCompanies: number[] = [];
  private selectedMinesites: number[] = [];
  private selectedPermissions: number[] = [];
  private ableToPrint = true;
  private notAbleToPrint = true;

  constructor(
    private _signalRService: SignalRService,
    private _signalREventService: SignalREventService,
    private _toastrService: ToastrService
  ) {
    this.registerOnServerEvents();

    this._signalRService.invoke("GetSections").then((sections: string[]) => {
      this.sections = sections;
    });
  }

  private onUsers(info: any): void {
    this.totalUsers = info.Data.Total;

    this.users.init();

    for (const item of info.Data.Users) {
      const user: User = $.extend(new User(), item);
      for (const section of user.SectionPermissions) {
        user.Sections.push($.extend(new SectionPermission(), section));
      }
      this.users.push(user);
    }

    this.usersChange.emit(this.users.Data);
  }

  private onPermissions(info: any): void {
    this.permissions.init();
    for (const item of info.Data.Permissions) {
      const permission: Permission = $.extend(new Permission(), item);
      this.permissions.push(permission);
    }

    this.permissionChange.emit(this.permissions.Data);
  }

  private onLanguages(info: any): void {
    this.languages.init();
    for (const item of info.Data.Languages) {
      const language: Language = $.extend(new Language(), item);
      this.languages.push(language);
    }

    this.languageChange.emit(this.languages.Data);
  }

  private onUserChagne(info: any): void {
    const user: User = $.extend(new User(), info.Data);
    if (this.users.update(user)) {
      this.userChange.emit(user);
    }
  }

  private onCreateUser(data: any): void {
    const user = $.extend(new User(), data);
    this.userCreated.emit(user);
    this.getUsers(this.currentPage);
  }

  private onUserDelete(data: any): void {
    this.getUsers(this.currentPage);
  }

  private onUserSave(data: any): void {
    this.userCreated.emit(data.User);
    //this._signalRService.invoke("EndSaveUser", data.User.Id);
  }

  private onPrintChange(info: any): void {
    this.printChange.emit(true);
  }

  //private onFilteredMinesites(info: any): void {
  //	this.minesiteids.emit(info);
  //}

  public toggleUser(userId: number, active: boolean, byUser: string): void {
    this._signalRService.invoke("ToggleUserState", userId, active, byUser);
  }

  public resetPassword(userId: number): void {
    this._signalRService.invoke("ResetPassword", userId);
  }

  public resetPasswordandQuestions(userId: number): void {
    this._signalRService.invoke("ResetPasswordAndQuestions", userId);
  }

  public getPermissions(): void {
    this._signalRService.invoke("GetPermissions");
  }

  public getLanguages(): void {
    this._signalRService.invoke("GetUserLanguages");
  }

  public getUsers(page: number): void {
    this.currentPage = page;
    this._signalRService.invoke(
      "FilterUsers",
      this.currentPage,
      this.orderBy,
      this.ascending,
      this.name,
      this.allEmployees,
      this.selectedCompanies,
      this.selectedMinesites,
      this.selectedPermissions,
      this.ableToPrint,
      this.notAbleToPrint
    );
  }

  public getUser(userId: number): void {
    this._signalRService.invoke("GetUser", userId);
  }

  public selectUser(user: User): void {
    this.userSelectedChange.emit(user);
  }

  public clearAll(): void {
    this.clearSelected.emit(true);
  }

  public allowUsersToPrint(selectedUsers: number[]): void {
    this._signalRService.invoke("AllowUsersToPrint", selectedUsers);
    //this.printChange.emit(true);
  }

  public restrictUsersFromPrint(selectedUsers: number[]): void {
    this._signalRService.invoke("RestrictUsersFromPrint", selectedUsers);
    //this.printChange.emit(true);
  }

  public createUser(user: User): void {
    this._signalRService.invoke(
      "CreateUser",
      user.FirstName,
      user.LastName,
      user.UserName,
      user.PermissionId,
      user.CreatedBy,
      user.LanguageName,
      user.CompanyId,
      user.MinesiteId,
      user.AllowPrinting,
      user.ResetPassword,
      user.ResetSecurityQuestions
    );
  }

  public changeUsername(oldUserName: string, newUserName: string): void {
    this._signalRService.invoke("ChangeUserName", oldUserName, newUserName);
  }

  public deleteUser(userId: number, byUser: string): void {
    this._signalRService.invoke("DeleteUser", userId, byUser);
  }

  public save(user: User): void {
    this._signalRService.invoke(
      "SaveUser",
      user.Id,
      user.FirstName,
      user.LastName,
      user.MinesiteId,
      user.CompanyId,
      user.PermissionId,
      user.LanguageName,
      user.AllowPrinting,
      user.ResetPassword,
      user.ResetSecurityQuestions
    );
  }

  public saveSectionPermissions(
    userId: number,
    sectionPermissions: SectionPermission[]
  ): void {
    for (const sectionPermission of sectionPermissions) {
      this._signalRService.invoke(
        "SaveSectionPermission",
        userId,
        sectionPermission.SectionId,
        sectionPermission.Allowed
      );
    }
  }

  public applyFilter(
    name: string,
    allEmployees: boolean,
    selectedCompanies: number[],
    selectedMinesites: number[],
    selectedPermissions: number[],
    ableToPrint: boolean,
    notAbleToPrint: boolean
  ): void {
    this.name = name;
    this.allEmployees = allEmployees;
    this.selectedCompanies = selectedCompanies;
    this.selectedMinesites = selectedMinesites;
    this.selectedPermissions = selectedPermissions;
    this.ableToPrint = ableToPrint;
    this.notAbleToPrint = notAbleToPrint;
    this._signalRService.invoke(
      "FilterUsers",
      this.currentPage,
      this.orderBy,
      this.ascending,
      this.name,
      this.allEmployees,
      this.selectedCompanies,
      this.selectedMinesites,
      this.selectedPermissions,
      this.ableToPrint,
      this.notAbleToPrint
    );
  }

  private registerOnServerEvents() {
    this._signalREventService.register("UserService", "users", (info: any) =>
      this.onUsers(info)
    );
    this._signalREventService.register(
      "UserService",
      "permissions",
      (info: any) => this.onPermissions(info)
    );
    this._signalREventService.register(
      "UserService",
      "userChange",
      (info: any) => this.onUserChagne(info)
    );
    this._signalREventService.register(
      "UserService",
      "userCreate",
      (info: any) => this.onCreateUser(info)
    );
    this._signalREventService.register(
      "UserService",
      "userLanguages",
      (info: any) => this.onLanguages(info)
    );
    this._signalREventService.register("UserService", "userSave", (info: any) =>
      this.onUserSave(info)
    );
    this._signalREventService.register(
      "UserService",
      "userDelete",
      (info: any) => this.onUserDelete(info)
    );
    this._signalREventService.register(
      "UserService",
      "printChange",
      (info: any) => this.onPrintChange(info)
    );
    //this._signalREventService.register("UserService", "minesiteids", (info: any) => this.onFilteredMinesites(info));
  }

  public deregisterOnServerEvents(): void {
    this._signalREventService.deregister("UserService");
  }

  public filterMinesitesFor(selectedCompanies: number[]): Promise<Number[]> {
    this.selectedCompanies = selectedCompanies;
    return this._signalRService.invoke(
      "FilterMinesiteForCompanies",
      this.selectedCompanies
    );
  }
}
